/**
 * WHAT THE AUDIT TRAIL'S RECORDS ARE CALLED, for every screen of the console that prints an entry.
 *
 * The log files each entry under a subject code — `admin_user`, `maintenance_window`,
 * `payout_account` — and under an action code. Neither is anything an operator reads: a screen says
 * what the record IS ("Admin", "Maintenance window", "Payout account"). The Fund audit trail had
 * these words for its own subjects; Recent actions on Access control printed the code, because an
 * admin can act on any kind of record and the funds screen's words covered eight of them. So the
 * words are one map here, for every subject the platform files entries under, and each screen
 * reads it. A census (admin-actions-scope.test.tsx) holds the map to every subject the services
 * write, so a new one fails a test until it has words of its own.
 *
 * `codeAsWords` is the fallback for a code no screen has words for yet — a subject, or an action —
 * so a new kind of entry is never printed as a code.
 */

/** A code said as words: "LEDGER_REVERSAL_RAISED" reads "Ledger reversal raised". */
export const codeAsWords = (code: string): string => {
  const words = code.toLowerCase().replace(/[_.]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Every kind of record the platform files an audit entry under, in the words a screen prints. */
export const AUDIT_SUBJECT_WORDS: Readonly<Record<string, string>> = {
  /* Money: the first eight are the subjects the Fund audit trail keeps. */
  withdrawal: 'Withdrawal',
  manual_adjustment: 'Manual adjustment',
  deposit: 'Deposit',
  ledger_transaction: 'Ledger transaction',
  deposit_retry_attempt: 'Deposit retry attempt',
  payout_provider: 'Payout provider',
  payout_account: 'Payout account',
  payout_notification: 'StarPay notification',
  ledger_reversal: 'Ledger reversal',
  payment_method: 'Payment method',
  /* Players. */
  player: 'Player',
  player_note: 'Player note',
  player_restriction: 'Player restriction',
  responsible_gaming_limit: 'Responsible gaming limit',
  identity_record: 'Identity check',
  kyc_submission: 'KYC submission',
  /* Staff and access. */
  admin_user: 'Admin',
  admin: 'Admin',
  role: 'Role',
  audit_log: 'Audit log',
  /* The platform. */
  brand: 'Brand',
  setting: 'Setting',
  notification_setting: 'Notification setting',
  maintenance_window: 'Maintenance window',
  announcement: 'Announcement',
  hash_check: 'Hash check',
  support_ticket: 'Support ticket',
  report_run: 'Report run',
  report_schedule: 'Report schedule',
  /* Games. */
  game: 'Game',
  game_provider: 'Game provider',
  provider_reconciliation_issue: 'Provider reconciliation issue',
  /* Risk. */
  risk_rule: 'Risk rule',
  risk_alert: 'Risk alert',
  risk_case: 'Risk case',
  /* Promotions and points. */
  promotion: 'Promotion',
  bonus_grant: 'Bonus grant',
  earning_rule: 'Earning rule',
  points_adjustment: 'Points adjustment',
  points_reward: 'Points reward',
  points_redemption: 'Reward redemption',
  redemption: 'Reward redemption',
  /* Partners. */
  agent: 'Agent',
  agent_payout: 'Agent payout',
  affiliate: 'Affiliate',
  affiliate_plan: 'Affiliate plan',
  affiliate_period: 'Affiliate period',
  affiliate_payout: 'Affiliate payout',
  commission_plan: 'Commission plan',
  commission_period: 'Commission period',
  cpa_award: 'CPA award',
  partner_user: 'Partner portal login',
};

/** The record an entry is about, in words: its own, or the code said as words. */
export const auditSubjectWords = (subject: string): string =>
  AUDIT_SUBJECT_WORDS[subject] ?? codeAsWords(subject);
